import React, { useState } from "react";
import { ShieldCheck, Copy, Check, ArrowLeft, ArrowRight, User, Building2 } from "lucide-react";
import { T, mono, sans, FONT_IMPORT } from "./ERP";
import { supabase } from "./supabaseClient";

const genCode = () => `BB-${Math.random().toString(36).slice(2, 6).toUpperCase()}-${Math.floor(100 + Math.random() * 899)}`;


const TERMS = `By continuing you agree that Bellboy will process visitor, vehicle, and payment records on behalf of your society, that biometric enrollment for residents and staff only happens after that individual gives specific consent, and that your society retains ownership of its own data at all times. Subscription fees are billed monthly in advance and either plan can be changed from the admin dashboard at any time. Cancelling stops the next billing cycle; it does not delete historical records, which remain available for export.`;

/* ---------- shared inputs ---------- */
function Field({ label, ...props }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12.5, fontWeight: 700, color: T.ink, fontFamily: sans }}>
      {label}
      <input {...props} style={{ border: `2.5px solid ${T.ink}`, borderRadius: 10, padding: "11px 13px", fontSize: 14, fontFamily: sans, outline: "none" }} />
    </label>
  );
}
function Card({ children, style }) {
  return <div style={{ background: T.white, border: `2.5px solid ${T.ink}`, borderRadius: 16, boxShadow: `6px 6px 0 ${T.ink}`, padding: 28, width: "100%", maxWidth: 440, ...style }}>{children}</div>;
}
function PrimaryButton({ children, disabled, ...props }) {
  return (
    <button disabled={disabled} {...props} style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
      background: disabled ? "#DCD6C2" : T.yellow, color: T.ink, border: `2.5px solid ${T.ink}`, borderRadius: 10,
      padding: "13px 16px", fontSize: 14.5, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : `4px 4px 0 ${T.ink}`, fontFamily: sans, marginTop: 6,
    }}>
      {children}
    </button>
  );
}
function GhostButton({ children, ...props }) {
  return (
    <button {...props} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: T.indigo, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: sans, padding: 0 }}>
      {children}
    </button>
  );
}

export default function Login({ onAuthenticated, onCancel, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // "login" | "register"
  const [step, setStep] = useState("base"); // base -> role-select -> resident-code | admin-details -> plan -> terms
  const [copied, setCopied] = useState(false);

  const [base, setBase] = useState({ name: "", email: "", rollCode: "", password: "", confirmPassword: "" });
  const [role, setRole] = useState(null); // "resident" | "admin"
  const [residentCode] = useState(genCode);
  const [adminDetails, setAdminDetails] = useState({ societyName: "" });
  const [plan, setPlan] = useState("399");
  const [agreed, setAgreed] = useState(false);
  const [baseError, setBaseError] = useState("");

  const [loginFields, setLoginFields] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setMode("login"); setStep("base"); setBase({ name: "", email: "", rollCode: "", password: "", confirmPassword: "" }); setRole(null); setAdminDetails({ societyName: "" }); setPlan("399"); setAgreed(false); setLoginError(""); };

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setLoginError("");
    const email = loginFields.email.trim();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password: loginFields.password });
    if (error || !data.user) { setLoginError(error?.message || "No account matches that email and password."); setBusy(false); return; }
    const { data: memberships, error: memberError } = await supabase.from("society_members").select("role, society_id, societies(name, plan)").eq("user_id", data.user.id).eq("status", "active").in("role", ["admin", "committee"]);
    const membership = memberships?.[0];
    if (memberError || !membership) { await supabase.auth.signOut(); setLoginError("This website is restricted to approved society administrators."); setBusy(false); return; }
    const { data: profile } = await supabase.from("profiles").select("full_name, email").eq("id", data.user.id).maybeSingle();
    setBusy(false);
    onAuthenticated({ userId: data.user.id, role: membership.role, societyId: membership.society_id, societyName: membership.societies?.name, adminName: profile?.full_name || "Admin", plan: membership.societies?.plan || "399", email: profile?.email || email });
  };

  const handleBaseContinue = async () => {
    if (base.password.length < 4) { setBaseError("Password must be at least 4 characters."); return; }
    if (base.password !== base.confirmPassword) { setBaseError("Passwords don't match."); return; }
    setBaseError("");
    if (base.rollCode.trim()) {
      setBusy(true);
      const { error } = await supabase.auth.signUp({ email: base.email.trim(), password: base.password, options: { data: { full_name: base.name.trim(), role: "resident", roll_code: base.rollCode.trim() } } });
      setBusy(false);
      if (error) { setBaseError(error.message); return; }
      setStep("resident-linked");
    } else setStep("role-select");
  };

  const handleRoleSelect = async (r) => {
    setRole(r);
    if (r === "resident") {
      setBusy(true);
      const { error } = await supabase.auth.signUp({ email: base.email.trim(), password: base.password, options: { data: { full_name: base.name.trim(), role: "resident" } } });
      setBusy(false);
      if (error) { setBaseError(error.message); setStep("base"); return; }
    }
    setStep(r === "resident" ? "resident-code" : "admin-details");
  };

  const handleAdminDetailsContinue = () => {
    if (!adminDetails.societyName.trim()) return;
    setStep("plan");
  };

  const copyCode = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(residentCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const finishRegistration = async () => {
    setBusy(true);
    const societyName = adminDetails.societyName.trim();
    const adminName = base.name.trim() || "Admin";
    const { data, error } = await supabase.auth.signUp({ email: base.email.trim(), password: base.password, options: { data: { full_name: adminName, role: "admin" } } });
    if (error || !data.user) { setLoginError(error?.message || "Registration could not be completed."); setBusy(false); return; }
    if (!data.session) { setLoginError("Registration could not be completed. Please try logging in."); setBusy(false); return; }
    const { data: societyId, error: setupError } = await supabase.rpc("setup_admin_society", { p_name: societyName, p_plan: plan, p_terms_version: "2026-01" });
    if (setupError) { setLoginError(setupError.message); setBusy(false); return; }
    setBusy(false);
    onAuthenticated({ userId: data.user.id, role: "admin", societyId, societyName, adminName, email: base.email.trim(), plan });
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px",
      background: T.cream, fontFamily: sans, position: "relative",
      backgroundImage: "radial-gradient(rgba(20,22,46,0.14) 1.3px, transparent 1.3px)", backgroundSize: "18px 18px",
    }}>
      <style>{FONT_IMPORT}</style>

      {onCancel && (
        <button onClick={onCancel} style={{ position: "absolute", top: 24, left: 24, display: "flex", alignItems: "center", gap: 6, background: T.white, border: `2px solid ${T.ink}`, borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: sans, boxShadow: `3px 3px 0 ${T.ink}` }}>
          <ArrowLeft size={13} /> Back to site
        </button>
      )}

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: sans, fontWeight: 700, fontSize: 20, color: T.indigo }}>
          <span style={{ color: T.yellow, fontSize: 13 }}>●</span> Bellboy
        </div>

        {/* ---------------- LOGIN ---------------- */}
        {mode === "login" && (
          <Card>
            <h2 style={{ margin: "0 0 4px", fontSize: 20, fontFamily: sans }}>Welcome back</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: T.muted }}>Log in to your society's Bellboy dashboard.</p>
            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Email" type="email" required placeholder="you@example.com" value={loginFields.email} onChange={(e) => setLoginFields((s) => ({ ...s, email: e.target.value }))} />
              <Field label="Password" type="password" required placeholder="••••••••" value={loginFields.password} onChange={(e) => setLoginFields((s) => ({ ...s, password: e.target.value }))} />
              {loginError && <div style={{ fontSize: 12.5, color: T.red, fontWeight: 700 }}>{loginError}</div>}
              <PrimaryButton type="submit" disabled={busy}>{busy ? "Signing in…" : "Log in"} <ArrowRight size={15} /></PrimaryButton>
            </form>
            <div style={{ marginTop: 18, textAlign: "center", fontSize: 13 }}>
              New to Bellboy? <GhostButton onClick={() => { setMode("register"); setStep("base"); }}>Register</GhostButton>
            </div>
          </Card>
        )}

        {/* ---------------- REGISTER: base ---------------- */}
        {mode === "register" && step === "base" && (
          <Card>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Create your account</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: T.muted }}>Tell us who you are. If someone already gave you a roll code, enter it and we'll link you straight in.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Full name" required placeholder="Your name" value={base.name} onChange={(e) => setBase((s) => ({ ...s, name: e.target.value }))} />
              <Field label="Email" type="email" required placeholder="you@example.com" value={base.email} onChange={(e) => setBase((s) => ({ ...s, email: e.target.value }))} />
              <Field label="Roll code (optional)" placeholder="e.g. SNR-8841 — leave blank if you don't have one" value={base.rollCode} onChange={(e) => setBase((s) => ({ ...s, rollCode: e.target.value }))} />
              <Field label="Password" type="password" required placeholder="At least 4 characters" value={base.password} onChange={(e) => setBase((s) => ({ ...s, password: e.target.value }))} />
              <Field label="Confirm password" type="password" required placeholder="Re-enter your password" value={base.confirmPassword} onChange={(e) => setBase((s) => ({ ...s, confirmPassword: e.target.value }))} />
              {baseError && <div style={{ fontSize: 12.5, color: T.red, fontWeight: 700 }}>{baseError}</div>}
              <PrimaryButton onClick={handleBaseContinue} disabled={!base.name.trim() || !base.email.trim() || !base.password || !base.confirmPassword}>Continue <ArrowRight size={15} /></PrimaryButton>
            </div>
            <div style={{ marginTop: 18, textAlign: "center", fontSize: 13 }}>
              Already registered? <GhostButton onClick={reset}>Log in</GhostButton>
            </div>
          </Card>
        )}

        {/* ---------------- REGISTER: resident linked via roll code ---------------- */}
        {mode === "register" && step === "resident-linked" && (
          <Card style={{ textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: T.mint, border: `2.5px solid ${T.ink}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <ShieldCheck size={24} color={T.green} />
            </div>
            <h2 style={{ margin: "0 0 8px", fontSize: 19 }}>You're linked in</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13.5, color: T.muted, lineHeight: 1.6 }}>
              Roll code <strong style={{ fontFamily: mono, color: T.ink }}>{base.rollCode}</strong> has been recorded against your name. Your society admin will confirm your access from their dashboard.
            </p>
            <PrimaryButton onClick={onCancel}>Back to home</PrimaryButton>
          </Card>
        )}

        {/* ---------------- REGISTER: role select ---------------- */}
        {mode === "register" && step === "role-select" && (
          <Card>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>What are you?</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: T.muted }}>No roll code yet — tell us your role so we send you down the right path.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button onClick={() => handleRoleSelect("resident")} style={{ display: "flex", alignItems: "center", gap: 14, textAlign: "left", padding: 16, border: `2.5px solid ${T.ink}`, borderRadius: 12, background: T.cream, cursor: "pointer", boxShadow: `4px 4px 0 ${T.ink}` }}>
                <User size={22} color={T.indigo} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14.5 }}>Resident</div>
                  <div style={{ fontSize: 12, color: T.muted }}>I live in a society and want Bellboy — I'll get a code to hand my admin.</div>
                </div>
              </button>
              <button onClick={() => handleRoleSelect("admin")} style={{ display: "flex", alignItems: "center", gap: 14, textAlign: "left", padding: 16, border: `2.5px solid ${T.ink}`, borderRadius: 12, background: T.cream, cursor: "pointer", boxShadow: `4px 4px 0 ${T.ink}` }}>
                <Building2 size={22} color={T.indigo} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14.5 }}>Admin / Committee</div>
                  <div style={{ fontSize: 12, color: T.muted }}>I manage a society and want to set up Bellboy for it.</div>
                </div>
              </button>
            </div>
            <div style={{ marginTop: 18, textAlign: "center" }}>
              <GhostButton onClick={() => setStep("base")}><ArrowLeft size={13} /> Back</GhostButton>
            </div>
          </Card>
        )}

        {/* ---------------- REGISTER: resident dummy code ---------------- */}
        {mode === "register" && step === "resident-code" && (
          <Card style={{ textAlign: "center" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 19 }}>Share this with your admin</h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: T.muted }}>Your admin enters this in their dashboard to grant your access.</p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: T.cream, border: `2.5px dashed ${T.ink}`, borderRadius: 12, padding: "16px 14px", marginBottom: 16 }}>
              <span style={{ fontFamily: mono, fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>{residentCode}</span>
              <button onClick={copyCode} style={{ border: `2px solid ${T.ink}`, background: copied ? T.green : T.white, borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                {copied ? <Check size={14} color={T.white} /> : <Copy size={14} color={T.ink} />}
              </button>
            </div>
            <PrimaryButton onClick={onCancel}>Back to home</PrimaryButton>
          </Card>
        )}

        {/* ---------------- REGISTER: admin details ---------------- */}
        {mode === "register" && step === "admin-details" && (
          <Card>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Set up your society</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: T.muted }}>Quick check on your details, plus your society's name.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Name" value={base.name} onChange={(e) => setBase((s) => ({ ...s, name: e.target.value }))} />
              <Field label="Email" value={base.email} onChange={(e) => setBase((s) => ({ ...s, email: e.target.value }))} />
              <Field label="Society name" required placeholder="e.g. Sunrise Enclave" value={adminDetails.societyName} onChange={(e) => setAdminDetails({ societyName: e.target.value })} />
              <PrimaryButton onClick={handleAdminDetailsContinue} disabled={!adminDetails.societyName.trim()}>Continue <ArrowRight size={15} /></PrimaryButton>
            </div>
            <div style={{ marginTop: 18, textAlign: "center" }}>
              <GhostButton onClick={() => setStep("role-select")}><ArrowLeft size={13} /> Back</GhostButton>
            </div>
          </Card>
        )}

        {/* ---------------- REGISTER: plan ---------------- */}
        {mode === "register" && step === "plan" && (
          <Card style={{ maxWidth: 520 }}>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Choose your plan</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: T.muted }}>Switch plans anytime from the dashboard.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { id: "399", price: "₹399", tag: "per month", features: ["Full ERP dashboard", "Gate AI, billing, complaints", "Includes sponsored placements"] },
                { id: "499", price: "₹499", tag: "per month", features: ["Everything in ₹399", "Zero ads, anywhere", "Priority support"] },
              ].map((p) => (
                <button key={p.id} onClick={() => setPlan(p.id)} style={{
                  textAlign: "left", padding: 16, borderRadius: 12, cursor: "pointer", fontFamily: sans,
                  border: `2.5px solid ${T.ink}`, background: plan === p.id ? T.yellow : T.white,
                  boxShadow: plan === p.id ? `4px 4px 0 ${T.ink}` : "none",
                }}>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono }}>{p.price}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>{p.tag}</div>
                  {p.features.map((f) => (
                    <div key={f} style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "flex-start", marginTop: 5 }}>
                      <Check size={13} color={T.green} style={{ flexShrink: 0, marginTop: 1 }} /> {f}
                    </div>
                  ))}
                </button>
              ))}
            </div>
            <PrimaryButton onClick={() => setStep("terms")}>Continue with ₹{plan} <ArrowRight size={15} /></PrimaryButton>
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <GhostButton onClick={() => setStep("admin-details")}><ArrowLeft size={13} /> Back</GhostButton>
            </div>
          </Card>
        )}

        {/* ---------------- REGISTER: terms ---------------- */}
        {mode === "register" && step === "terms" && (
          <Card>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Terms & conditions</h2>
            <div style={{ margin: "14px 0", padding: 14, background: T.cream, border: `2px solid ${T.ink}`, borderRadius: 10, fontSize: 12.5, lineHeight: 1.6, color: T.ink, maxHeight: 180, overflowY: "auto" }}>
              {TERMS}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 4 }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ width: 18, height: 18 }} />
              I've read and agree to the terms & conditions
            </label>
            <PrimaryButton onClick={finishRegistration} disabled={!agreed || busy}>{busy ? "Creating…" : "Enter my dashboard"} <ArrowRight size={15} /></PrimaryButton>
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <GhostButton onClick={() => setStep("plan")}><ArrowLeft size={13} /> Back</GhostButton>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}